import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Text, Badge, Switch } from "@chakra-ui/react";
import { Plus, EllipsisVertical, Eye, Pencil } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import AddAppVersionDrawer from "./components/AddAppVersionDrawer";
import ViewAppVersionDrawer from "./components/ViewAppVersionDrawer";
import EditAppVersionDrawer from "./components/EditAppVersionDrawer";
import { formatDate } from "utils/dateFormatter";

import { useDispatch, useSelector } from "react-redux";
import {
    fetchAppVersionsAction,
    createAppVersionAction,
    updateAppVersionAction,
} from "state/actions/appVersionActions";

const ManageAppVersionsTab = () => {
    const dispatch = useDispatch();
    const { appVersions, error, loading } = useSelector((state) => state.appVersions);

    const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
    const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
    const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);

    useEffect(() => {
        dispatch(fetchAppVersionsAction("android"));
    }, [dispatch]);

    const handleOpenAddDrawer = () => {
        setAddDrawerOpen(true);
    };

    const handleCloseAddDrawer = () => {
        setAddDrawerOpen(false);
    };

    const handleAddVersion = async (newVersion) => {
        return await dispatch(createAppVersionAction(newVersion));
    };

    const handleViewVersion = (version) => {
        setSelectedVersion(version);
        setViewDrawerOpen(true);
    };

    const handleEditVersion = (version) => {
        setSelectedVersion(version);
        setEditDrawerOpen(true);
    };

    const handleUpdateVersion = async (updatedVersion) => {
        return await dispatch(
            updateAppVersionAction(updatedVersion.version_id, updatedVersion)
        );
    };

    const handleToggleMaintenanceMode = (version) => {
        const updatedPayload = {
            platform: version.platform,
            latest_version: version.latest_version,
            min_supported_version: version.min_supported_version,
            store_url: version.store_url,
            maintenance_mode: !version.maintenance_mode,
        };

        dispatch(updateAppVersionAction(version.version_id, updatedPayload));
    };

    const generateMenuItems = (row) => [
        {
            icon: <Eye size={16} />,
            text: "View App Version",
            onClick: () => handleViewVersion(row.original),
        },
        {
            icon: <Pencil size={16} />,
            text: "Edit App Version",
            onClick: () => handleEditVersion(row.original),
        },
    ];

    const columnsData = [
        {
            Header: "Platform",
            accessor: "platform",
        },
        {
            Header: "Latest Version",
            accessor: "latest_version",
        },
        {
            Header: "Min Supported",
            accessor: "min_supported_version",
        },
        {
            Header: "Store URL",
            accessor: "store_url",
            Cell: ({ value }) => (
                <Text isTruncated maxWidth="220px">
                    {value}
                </Text>
            ),
        },
        {
            Header: "Created At",
            accessor: "created_at",
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Box>
            <Flex justifyContent="end" mb={4}>
                {loading ? (
                    <Skeleton width="170px" height="40px" borderRadius="md" />
                ) : (
                    <GradientButton
                        icon={<Plus size={16} />}
                        height={"40px"}
                        mr={5}
                        onClick={handleOpenAddDrawer}
                    >
                        Add App Version
                    </GradientButton>
                )}
            </Flex>

            <ReusableTable
                columnsData={columnsData}
                tableData={appVersions || []}
                isLoading={loading}
            />

            <AddAppVersionDrawer
                isOpen={isAddDrawerOpen}
                onClose={handleCloseAddDrawer}
                onAddVersion={handleAddVersion}
            />

            <ViewAppVersionDrawer
                isOpen={isViewDrawerOpen}
                onClose={() => setViewDrawerOpen(false)}
                appVersion={selectedVersion}
            />

            <EditAppVersionDrawer
                isOpen={isEditDrawerOpen}
                onClose={() => setEditDrawerOpen(false)}
                appVersion={selectedVersion}
                onUpdateVersion={handleUpdateVersion}
            />
        </Box>
    );
};

export default ManageAppVersionsTab;